import { DealsListItemData, SidesScore, TextureData } from "../../../types/types";
import { ISize } from "../../../controls/scrollpane/ISize";
import { RectSize } from "../../../utils/RectSize";
import { SettingsItem } from "../options/SettingsItem";
import { AppSettings } from "../../managers/AppSettings";
import { LabelButton } from "../../../ui/LabelButton";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { ContractResultToBubbleFrame, ContractResultToContractLabel } from "./DealCompleteDealsContent";
import { DealListItemContractItem } from "./DealListItemContractItem";
import { AppLocalization } from "../../localization/AppLocalization";
import { Players } from "../../player/Players";


export type BubbleListItemData = {
    label: string,
    success: boolean,
}

export class DealsListItem
    extends SettingsItem
    implements ISize {
    // NOTE: item1 is label Text
    public item2: DealListItemContractItem;
    public item3: Phaser.GameObjects.Text;
    // out|over = GREEN; disabled = RED;
    public item4: LabelButton;
    public item5: Phaser.GameObjects.Text;

    /**
     * reference width of any element (doesnt consider dynamic element size)
     * used to calc item's position in one of the 5 columns
     * - the calculation does consider borderPadding, 
     *   as padding for far left-right elements
     * NOTE: same as @see DealsHeaderItem::elementReferenceWidth
    */
    protected elementReferenceWidth: number = 70;

    protected bgTextureData: TextureData;
    protected labelTextStyle: Phaser.Types.GameObjects.Text.TextStyle;

    // delimiter line
    public imgDelimiterLine: Phaser.GameObjects.Image;
    protected hasDelimiterLine: boolean;
    protected delimiterLineAlpha: number;


    public Init(
        bgTextureData: TextureData = undefined,
        labelTextStyle: Phaser.Types.GameObjects.Text.TextStyle = undefined,
        delimiterLineAlpha: number,
    ): void {
        this.bgTextureData = bgTextureData;
        this.labelTextStyle = labelTextStyle;
        this.delimiterLineAlpha = delimiterLineAlpha;

        this.size = new RectSize();
    }

    public Build(): void {
        this.bg = this.scene.make.image({ key: this.bgTextureData.texture, frame: this.bgTextureData.frame });
        this.bg.setOrigin(0);
        this.add(this.bg);

        // this.label eg 'item1'
        this.label = this.scene.make.text({ text: "", style: this.labelTextStyle });
        this.label.setAlign("left");
        this.label.setOrigin(0, 0.5);
        this.add(this.label);

        // for Belote, Coinche, (?TODO:Contree?)
        this.item2 = new DealListItemContractItem(this.scene);
        this.item2.Init(this.labelTextStyle);
        this.item2.Build();
        // this.item2.setScale(0.9);
        this.add(this.item2);

        this.item3 = this.scene.make.text({ text: "", style: this.labelTextStyle });
        this.item3.setOrigin(0.5);
        this.add(this.item3);

        // always disabled. 
        // change texture (GREEN|RED|ORANGE?/tie?) to set bubble color
        this.item4 = new LabelButton(this.scene);
        this.item4.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "rect-rounded-green.png" },
            disabled: { texture: LoaderFileKeys.ui, frame: "rect-rounded-green.png" },
        }, false); // interactive? timeOutDelay?
        // NOTE: other textfield items in this item are 28px font size
        // 35px * 0.8 (baseScale) = 28px
        this.item4.InitLabel({
            out: {
                color: AppSettings.colors.whiteString,
                // fontSize: "36px",
                // fontFamily: AppSettings.fontFamilies.MontserratBold,
                // metrics: AppSettings.metrics.montserratBold36,
                fontSize: this.labelTextStyle.fontSize,
                fontFamily: this.labelTextStyle.fontFamily,
                metrics: this.labelTextStyle.metrics,
            },
        });
        this.item4.label.setOrigin(0.5);
        this.item4.PostInit();
        // this.item4.setScale(0.8);
        // this.item4.view.displayWidth = UI_LISTITEM_BUBBLE_WIDTH;
        this.add(this.item4);

        this.item5 = this.scene.make.text({ text: "", style: this.labelTextStyle });
        this.item5.setAlign("center");
        this.item5.setOrigin(1, 0.5);
        this.add(this.item5);

        this.imgDelimiterLine = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-white.png" });
        this.imgDelimiterLine.setOrigin(0, 0.5);
        this.imgDelimiterLine.alpha = this.delimiterLineAlpha;
        this.add(this.imgDelimiterLine);
    }

    public SetData(
        inputData: DealsListItemData,
        hasDelimiterLine: boolean,
    ): void {
        // console.log("DealsListItem::SetData");
        // console.log(inputData);

        this.hasDelimiterLine = hasDelimiterLine;
        this.imgDelimiterLine.visible = hasDelimiterLine;

        const playerName: string = inputData.attack.playerName.replace(Players.PLAYER_NAME, AppLocalization.GetSharedData(AppLocalization.GetCurLanguageId()).general.playerName);
        const playerAxis: string = AppLocalization.GetSharedData().general.playersAxes[inputData.attack.axisIndex];
        this.label.text = playerName + "\n(" + playerAxis + ")";
        this.item2.SetData(inputData.contract);
        this.item3.text = inputData.points.us + " - " + inputData.points.them;
        this.item4.label.text = ContractResultToContractLabel(inputData.result);
        this.item4.view.setFrame(ContractResultToBubbleFrame(inputData.result));
        this.item5.text = inputData.score.us + " - " + inputData.score.them;
    }

    public override SetWidthGetHeight(width: number): number {
        this.label.style.setWordWrapWidth(width / 5);
        this.item3.style.setWordWrapWidth(width / 5);
        this.item5.style.setWordWrapWidth(width / 5);

        const height = Math.max(
            this.label.displayHeight + 2 * this.borderPadding.vertical,
            this.item3.displayHeight + 2 * this.borderPadding.vertical,
            this.item5.displayHeight + 2 * this.borderPadding.vertical
        );

        // set bg (green|red|orange bubble) height
        // this.item4.view.displayHeight = height - 2 * UI_LISTITEM_BUBBLE_PADDING_VERTICAL;

        return height;
    }

    public override OnResize(): void {
        // console.log("DealsListItem::OnResize");

        const centerX = this.size.width / 2;
        const centerY = this.size.height / 2;
        const item2PosX = this.borderPadding.horizontal + this.elementReferenceWidth / 2 + (this.size.width / 2 - this.borderPadding.horizontal - this.elementReferenceWidth / 2) / 2;
        const item4PosX = this.size.width - this.borderPadding.horizontal - this.elementReferenceWidth / 2 - (this.size.width / 2 - this.borderPadding.horizontal - this.elementReferenceWidth / 2) / 2;

        this.bg.setDisplaySize(this.size.width, this.size.height);

        // delimiter (if item has it -- last item in list does not have it)
        if (this.hasDelimiterLine) {
            // origin 0,1 (bot left)
            this.imgDelimiterLine.y = this.size.height;
            this.imgDelimiterLine.displayWidth = this.size.width;
        }

        // label origin (0, 0.5)
        this.label.setPosition(this.borderPadding.horizontal, centerY);

        this.item2.setPosition(item2PosX, centerY);

        this.item3.setPosition(centerX, centerY);

        this.item4.setPosition(item4PosX, centerY);

        // this.item5.setPosition(this.size.width - this.borderPadding.horizontal - this.elementReferenceWidth/2, centerY);
        this.item5.setPosition(this.size.width - this.borderPadding.horizontal, centerY);
    }
}